import React, { useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import courseRunShape from '@prospectus/common/types/courseRunShape';
import courseShape from '@prospectus/common/types/courseShape';
import activeProgramShape from '@prospectus/common/types/activeProgramShape';
import ProgramUpsell from '@prospectus/open-course-page/components/ProgramUpsell';

import { pageTrackingObjectUpdate } from '@utils/cohesion/helpers';
import CourseHeader from './CourseHeader';
import CourseSnapshot from './CourseSnapshot';
import CourseRunSelector from './CourseRunSelector';
import StickyNav from './StickyNav';
import CourseMain from './CourseMain/variants/DesktopVariant';
import Instructors from './Instructors/variants/DesktopVariant';
import FAQ from './FAQ';
import CourseModes from './CourseModes';
import SocialShare from './SocialShare';
import BusinessBlock from './BusinessBlock/variants/DesktopVariant';

function CoursePage({
  courseRun,
  course,
  activeCourseRun,
  refMap,
  semanticRefMap,
  scrollToRef,
  showCourseModes,
  emailOptIn,
  setEmailOptIn,
  sortedPrograms,
  sharedMessages,
  paymentOption,
  webview,
  userLoggedIn,
  isEnterpriseUser,
}) {
  useEffect(() => {
    pageTrackingObjectUpdate('product-page');
  }, []);
  // Until activeCourseRun is not set in state, the value is default to {}. The length check on keys
  // is needed to ensure that value is properly available in state.
  const hasInstructors = Object.keys(activeCourseRun).length > 0 && activeCourseRun.staff.length > 0;

  // Hardcoded setting the flag to false so that the recommendations are not shown.
  // This will be used by student engagement team for Cross Product Recommendation.
  const showProgramUpsell = !webview && sortedPrograms.length !== 0 && (!userLoggedIn || isEnterpriseUser === false);

  return (
    <div className="course-about desktop course-info-content" data-course-run-key={courseRun.key}>
      {/* 'key' has to go after '...course' in props to avoid the react key getting
      overriden by the course key */}
      <CourseHeader {...course} />
      <CourseSnapshot {...course} paymentOption={paymentOption} hasPricingColumn activeCourseRun={activeCourseRun} />
      <CourseRunSelector
        {...course}
        activeCourseRuns={course.activeCourseRuns}
        refProp={refMap.enrollRef}
        emailOptIn={emailOptIn}
        setEmailOptIn={setEmailOptIn}
      />
      <StickyNav
        {...course}
        scrollToRef={scrollToRef}
        semanticRefMap={semanticRefMap}
        showCourseModes={showCourseModes}
        emailOptIn={emailOptIn}
      />
      <CourseMain
        refMapProp={{
          aboutRef: refMap.aboutRef,
          learnRef: refMap.learnRef,
          syllabusRef: refMap.syllabusRef,
        }}
        course={course}
        programs={sortedPrograms}
        commonMessages={sharedMessages}
      />
      {!!hasInstructors && <Instructors refProp={refMap.instructorsRef} staff={activeCourseRun.staff} />}
      <div className={classNames({ 'mb-lg-5.5': !course.coursePrice })}>
        <FAQ refProp={refMap.faqsRef} commonMessages={sharedMessages} {...course} key={`${courseRun.key}-faq`} />
      </div>
      <ProgramUpsell programs={sortedPrograms} showProgramUpsell={showProgramUpsell} courseKey={courseRun.key} />
      {course.coursePrice && (
      <CourseModes
        {...course}
        refProp={refMap.waysRef}
        showCourseModes={showCourseModes}
        emailOptIn={emailOptIn}
        showProgramUpsell={showProgramUpsell}
      />
      )}
      {activeCourseRun.marketingUrl && <SocialShare {...course} />}
      {!webview && <BusinessBlock {...course} key={`${courseRun.key}-business`} />}
    </div>
  );
}

CoursePage.propTypes = {
  courseRun: PropTypes.shape(courseRunShape).isRequired,
  course: PropTypes.shape(courseShape).isRequired,
  activeProgram: PropTypes.shape(activeProgramShape),
  activeCourseRun: PropTypes.shape(courseRunShape).isRequired,
  refMap: PropTypes.shape().isRequired,
  semanticRefMap: PropTypes.shape().isRequired,
  scrollToRef: PropTypes.func.isRequired,
  showCourseModes: PropTypes.bool.isRequired,
  emailOptIn: PropTypes.bool.isRequired,
  setEmailOptIn: PropTypes.func.isRequired,
  sortedPrograms: PropTypes.arrayOf(PropTypes.shape(activeProgramShape)).isRequired,
  sharedMessages: PropTypes.shape().isRequired,
  paymentOption: PropTypes.string.isRequired,
  webview: PropTypes.bool,
  userLoggedIn: PropTypes.bool,
  isEnterpriseUser: PropTypes.bool,
};

CoursePage.defaultProps = {
  activeProgram: null,
  webview: false,
  userLoggedIn: false,
  isEnterpriseUser: false,
};

export default CoursePage;
